import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useCallback, useMemo, useState } from 'react';
import useGridExport from 'src/hooks/useGridExport';
import { DataGrid, GridToolbarV2, useGridRows } from 'src/modules/grids';
import { useMyAddressColumns } from './hooks/useMyAddressColumns';
import { MyAddressDrawer } from './MyAddressDrawer';
import {
  MY_ADDRESSES_GRID_STORAGE_KEY,
  OrgPlace,
  useDeleteOrgPlace,
  useOrgPlaces,
  useUpdateOrgPlace,
} from './myAddresses.api';

/** My Addresses, one server page at a time (TAS-3660 P7 BZ76); a failed read shows Retry through `reader`. */
export default function MyAddressesGrid() {
  const apiRef = useGridApiRef();
  const enhancedGridState = useGridRows({ mode: 'paged', storageKey: MY_ADDRESSES_GRID_STORAGE_KEY });
  const { places, loading, pageInfo, totalCount, fetchAllForExport, reader } = useOrgPlaces(enhancedGridState);
  // The header Export button (#7627 F20) writes every address under the current search and filters.
  useGridExport(apiRef, 'my-addresses', undefined, undefined, fetchAllForExport);
  const [editingAddress, setEditingAddress] = useState<OrgPlace | null>(null);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');

  const { updatePlace } = useUpdateOrgPlace();
  const { deletePlace } = useDeleteOrgPlace();

  const handleEdit = useCallback((address: OrgPlace) => {
    setEditingAddress(address);
    setDrawerMode('edit');
  }, []);

  const handleToggleDisabled = useCallback(
    (address: OrgPlace) => {
      updatePlace(address.id, { disabled: !address.disabled });
    },
    [updatePlace]
  );

  const handleDelete = useCallback(
    (addressId: number) => {
      deletePlace(addressId);
    },
    [deletePlace]
  );

  const handleCloseDrawer = useCallback(() => {
    setEditingAddress(null);
    setDrawerMode('create');
  }, []);

  const columns = useMyAddressColumns({
    onEdit: handleEdit,
    onToggleDisabled: handleToggleDisabled,
    onDelete: handleDelete,
  });

  const toolbar = useMemo(
    () => (
      <GridToolbarV2
        apiRef={apiRef}
        enhancedGridState={enhancedGridState}
        entityName="Address"
        entityNamePlural="Addresses"
        toolbarActionsRight={
          <MyAddressDrawer mode={drawerMode} addressData={editingAddress} onClose={handleCloseDrawer} />
        }
      />
    ),
    [apiRef, enhancedGridState, drawerMode, editingAddress, handleCloseDrawer]
  );

  return (
    <>
      {reader}
      <DataGrid
        mode="paged"
        rows={places}
        totalCount={totalCount}
        pageInfo={pageInfo}
        loading={loading}
        enhancedGridState={enhancedGridState}
        columns={columns}
        gridKey="settings.business.my-addresses"
        storageKey={MY_ADDRESSES_GRID_STORAGE_KEY}
        apiRef={apiRef}
        slots={{ toolbar }}
        variant="list"
      />
    </>
  );
}
